import type { PlaybookChannelConfig } from './channelConfig';
import {
  INSIGHT_TEMPLATE_STATE,
  type InsightTemplateState,
  PLAYBOOK_GENERATION_RUN_STATUS,
  PLAYBOOK_GENERATION_SOURCE_KIND,
  PLAYBOOK_INSIGHT_KIND,
  PLAYBOOK_INSIGHT_RUN_STATUS,
  PLAYBOOK_INSIGHT_SOURCE,
  PLAYBOOK_INSIGHT_STEP,
  PLAYBOOK_SOURCE_BLOCKED_REASON,
  PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON,
  type PlaybookGenerationRunStatus,
  type PlaybookGenerationSourceKind,
  type PlaybookInsightKind,
  type PlaybookInsightRunStatus,
  type PlaybookInsightSource,
  type PlaybookInsightStep,
  PLAYBOOKS_WIZARD_STEP,
  type PlaybookSourceBlockedReason,
  type PlaybooksWizardStep,
  type PlaybookTemplateRunNoIdeasReason,
} from './const';
import { isPlaybookInsightRunProcessing, isPlaybookInsightRunReadyForReview } from './insights';
import {
  type PlaybookGenerationSource,
  type PlaybookInsightRun,
  type PlaybookInsightRunCoverage,
} from './types';

const PLAYBOOKS_WIZARD_STEP_LABEL: Record<PlaybooksWizardStep, string> = {
  [PLAYBOOKS_WIZARD_STEP.context]: 'Context',
  [PLAYBOOKS_WIZARD_STEP.audiences]: 'Target audiences',
  [PLAYBOOKS_WIZARD_STEP.playbooks]: 'Playbooks',
};

export const getPlaybooksWizardStepLabel = (step: PlaybooksWizardStep) =>
  PLAYBOOKS_WIZARD_STEP_LABEL[step];

// The wait-screen message shown in the Setup playbooks wizard while the run generates — one per
// processing status, so the modal reads as a sequence (crawl → audiences → playbooks).
const PLAYBOOK_GENERATION_ANALYZING_MESSAGE: Partial<Record<PlaybookGenerationRunStatus, string>> =
  {
    [PLAYBOOK_GENERATION_RUN_STATUS.collectingContext]: 'Crawling and scanning…',
    [PLAYBOOK_GENERATION_RUN_STATUS.contextLocked]: 'Crawling and scanning…',
    [PLAYBOOK_GENERATION_RUN_STATUS.generatingAudiences]: 'Generating audience suggestions…',
    [PLAYBOOK_GENERATION_RUN_STATUS.generatingPlaybooks]: 'Generating playbook suggestions…',
  };

export const getPlaybookGenerationAnalyzingMessage = (
  status?: PlaybookGenerationRunStatus
): string => (status && PLAYBOOK_GENERATION_ANALYZING_MESSAGE[status]) || 'Crawling and scanning…';

// What the setup banner reports while the same run generates. A channel with no audiences step
// generates none, so its whole run is about playbooks.
export const getPlaybookGenerationProgressLabel = (
  status: PlaybookGenerationRunStatus,
  config: PlaybookChannelConfig
): string =>
  config.wizardSteps.includes(PLAYBOOKS_WIZARD_STEP.audiences) &&
  status !== PLAYBOOK_GENERATION_RUN_STATUS.generatingPlaybooks
    ? 'generating audiences'
    : 'generating playbooks';

const PLAYBOOKS_INSIGHT_STEP_LABEL: Record<PlaybookInsightStep, string> = {
  [PLAYBOOK_INSIGHT_STEP.copyReview]: 'A/B tests',
  [PLAYBOOK_INSIGHT_STEP.insightsReview]: 'Insights',
};

export const getPlaybookInsightsStepLabel = (step: PlaybookInsightStep) =>
  PLAYBOOKS_INSIGHT_STEP_LABEL[step];

// The wait-screen message while a run is processing. The Anyword source generates copy and then
// analyzes how it scored; the integration source has no copy step and analyzes what the channel
// already published.
export const getPlaybookInsightAnalyzingMessage = (
  config: PlaybookChannelConfig,
  status?: PlaybookInsightRunStatus,
  source?: PlaybookInsightSource
): string => {
  if (source === PLAYBOOK_INSIGHT_SOURCE.integration) {
    return `Analyzing ${config.itemLabel} performance…`;
  }
  if (source === PLAYBOOK_INSIGHT_SOURCE.manualData) {
    return 'Analyzing uploaded A/B tests…';
  }
  return status === PLAYBOOK_INSIGHT_RUN_STATUS.analyzing
    ? 'Learning insights from tests…'
    : 'Collecting and analyzing data…';
};

// A run pauses at two human gates — first on the generated copy, then on the insights drawn from
// it — so the ready state names whichever is actually waiting.
const PLAYBOOK_INSIGHT_RUN_REVIEW_SUBJECT: Partial<Record<PlaybookInsightRunStatus, string>> = {
  [PLAYBOOK_INSIGHT_RUN_STATUS.copyReady]: 'A/B tests',
  [PLAYBOOK_INSIGHT_RUN_STATUS.insightsReady]: 'Insights',
};

const getReviewSubject = (status?: PlaybookInsightRunStatus): string =>
  (status && PLAYBOOK_INSIGHT_RUN_REVIEW_SUBJECT[status]) || 'Insights';

export const getPlaybookRunReadyTitle = (status?: PlaybookInsightRunStatus): string =>
  `${getReviewSubject(status)} are ready for review`;

// What a run says about itself in one line — working, or waiting for the user. Null once it is past
// both, when there is nothing left to report that its results do not already say.
export const getPlaybookRunStatusLabel = (
  config: PlaybookChannelConfig,
  run?: PlaybookInsightRun | null
): string | null => {
  if (isPlaybookInsightRunProcessing(run?.status)) {
    return getPlaybookInsightAnalyzingMessage(config, run?.status, run?.source);
  }
  if (isPlaybookInsightRunReadyForReview(run?.status)) {
    return getPlaybookRunReadyTitle(run?.status);
  }
  return null;
};

// How an insight names where it came from. Uploaded A/B tests read as integration data to the user.
const PLAYBOOKS_INSIGHT_SOURCE_LABEL: Record<PlaybookInsightSource, string> = {
  [PLAYBOOK_INSIGHT_SOURCE.anyword]: 'Anyword data',
  [PLAYBOOK_INSIGHT_SOURCE.integration]: 'Integration data',
  [PLAYBOOK_INSIGHT_SOURCE.manualData]: 'Integration data',
};

export const getPlaybookInsightsSourceLabel = (source: PlaybookInsightSource) =>
  PLAYBOOKS_INSIGHT_SOURCE_LABEL[source];

// The card's source boxes sit side by side, so the upload box needs its own name.
const PLAYBOOK_SOURCE_BOX_LABEL: Record<PlaybookInsightSource, string> = {
  ...PLAYBOOKS_INSIGHT_SOURCE_LABEL,
  [PLAYBOOK_INSIGHT_SOURCE.manualData]: 'Performance results file',
};

export const getPlaybookSourceBoxLabel = (source: PlaybookInsightSource) =>
  PLAYBOOK_SOURCE_BOX_LABEL[source];

export const getPlaybookInsightRunTitle = (playbookName?: string): string =>
  playbookName ? `Generate insights for ${playbookName}` : 'Generate insights';

// Which data the run reads. An integration run is pointed at one account, so it names it; the
// account name is resolved from the workspace's accounts and may not be there yet.
export const getPlaybookInsightSourceDetail = ({
  source,
  config,
  accountName,
}: {
  source: PlaybookInsightSource;
  config: PlaybookChannelConfig;
  accountName?: string;
}): string => {
  if (source === PLAYBOOK_INSIGHT_SOURCE.manualData) {
    return `Source: ${PLAYBOOK_SOURCE_BOX_LABEL[source]}`;
  }
  if (source !== PLAYBOOK_INSIGHT_SOURCE.integration) {
    return `Source: ${PLAYBOOKS_INSIGHT_SOURCE_LABEL[PLAYBOOK_INSIGHT_SOURCE.anyword]}`;
  }

  return accountName
    ? `Source: ${config.accountLabel}: ${accountName}`
    : `Source: ${config.accountLabel}`;
};

const PLAYBOOK_SOURCE_BLOCKED_TOOLTIP: Record<PlaybookSourceBlockedReason, string> = {
  [PLAYBOOK_SOURCE_BLOCKED_REASON.runInProgress]:
    'Another insights run is already in progress for this playbook',
  [PLAYBOOK_SOURCE_BLOCKED_REASON.anywordRequired]: 'Generate insights from Anyword data first',
};

export const getPlaybookSourceBlockedTooltip = (reason: PlaybookSourceBlockedReason): string =>
  PLAYBOOK_SOURCE_BLOCKED_TOOLTIP[reason];

const pluralize = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

// The units an integration run's coverage is counted in, named the way its channel names them.
const formatCoverageUnits = (
  coverage: PlaybookInsightRunCoverage,
  config: PlaybookChannelConfig
) => {
  const copies = coverage.uniqueCopies ?? 0;

  return {
    blocks: pluralize(coverage.blocks ?? 0, config.blockLabel, config.blockLabelPlural),
    items: pluralize(coverage.items ?? 0, config.itemLabel, config.itemLabelPlural),
    copies: pluralize(copies, config.itemLabel, config.itemLabelPlural),
    assets: pluralize(copies, 'unique asset', 'unique assets'),
  };
};

// How much of the customer's real performance data an integration run drew on. Shown with the
// insights under review, because the numbers are what make their low validity self-evident.
export const getInsightCoverageText = (
  coverage: PlaybookInsightRunCoverage,
  config: PlaybookChannelConfig
): string => {
  const { blocks, items, copies, assets } = formatCoverageUnits(coverage, config);

  return config.coverageSummary === 'copies'
    ? `Found ${copies}`
    : `Found ${blocks}, ${items} and ${assets}`;
};

// The not-enough-data answer of an integration run: which playbook it was for, then what it did
// find in the units its channel counts in — without the unique assets, a count that says nothing to
// someone looking at too little data.
export const getPlaybookNotEnoughDataMessage = (playbookName: string): string =>
  `We couldn’t find enough data in this integration to infer insights for this playbook: ${playbookName}.`;

export const getInsightCoverageFoundText = (
  coverage: PlaybookInsightRunCoverage,
  config: PlaybookChannelConfig
): string => {
  const { blocks, items, copies } = formatCoverageUnits(coverage, config);
  const found = config.coverageSummary === 'copies' ? copies : `${blocks}, ${items}`;

  return `What we did find: ${found}`;
};

// The fallback half of the not-enough-data answer, for a run that reported no coverage to describe —
// an Anyword run never has any, so without this its dialog would have nothing in the body at all.
// An upload shows no coverage: the backend counts it in ad account units, which are not the file's
// tests.
export const getPlaybookNotEnoughDataHint = (source: PlaybookInsightSource): string =>
  source === PLAYBOOK_INSIGHT_SOURCE.manualData
    ? 'Upload more tests that change the same text to generate insights.'
    : 'Run more variations to generate insights.';

export const getInsightsGeneratedMessage = (count: number): string =>
  `${count} ${count === 1 ? 'insight was' : 'insights were'} generated.`;

// The backend reports a failed run without saying why, so the reason is optional until it does.
export const getPlaybookGenerationFailedMessage = (reason?: string | null): string =>
  reason || 'Run has failed, discard it and try again.';

// Connected-account context stores an integration asset id, which is meaningless on screen — the
// card shows this fixed label instead of the source's value. Every channel that connects an
// account needs an entry here.
const PLAYBOOK_GENERATION_SOURCE_LABEL: Partial<Record<PlaybookGenerationSourceKind, string>> = {
  [PLAYBOOK_GENERATION_SOURCE_KIND.linkedinAds]: 'LinkedIn Ads',
  [PLAYBOOK_GENERATION_SOURCE_KIND.metaAds]: 'Meta Ads',
  [PLAYBOOK_GENERATION_SOURCE_KIND.linkedinSocial]: 'LinkedIn',
};

export const getPlaybookGenerationSourceLabel = (source: PlaybookGenerationSource): string =>
  PLAYBOOK_GENERATION_SOURCE_LABEL[source.sourceType] ?? source.fileName ?? source.value ?? '';

export const getPlaybookTemplatesTitle = (playbookName?: string): string =>
  playbookName ? `Build templates for ${playbookName}` : 'Build templates';

// Pattern insights come only from an Integration data run, so that kind names where to get one.
const UNIQUE_TEMPLATES_MISSING_INSIGHT_LABEL: Record<PlaybookInsightKind, string> = {
  [PLAYBOOK_INSIGHT_KIND.directional]: 'a performance insight',
  [PLAYBOOK_INSIGHT_KIND.pattern]: 'a pattern insight from the Integration data source',
};

// Why "Performance templates" is disabled, from the insight kinds the playbook has no approved
// insight of. A playbook that does not report them is treated as missing both. A pattern insight
// can only come from an Integration data run, so a playbook without that source is told to add it
// before anything about approving.
export const getUniqueTemplatesUnavailableMessage = (
  missing: PlaybookInsightKind[] | undefined,
  integrationSourceAdded: boolean
): string => {
  const missingPattern = !missing?.length || missing.includes(PLAYBOOK_INSIGHT_KIND.pattern);
  if (missingPattern && !integrationSourceAdded) {
    return 'Add account data source first';
  }

  return missing?.length === 1
    ? `Available after you approve ${UNIQUE_TEMPLATES_MISSING_INSIGHT_LABEL[missing[0]]}`
    : 'Available after you approve a performance insight and a pattern insight';
};

const PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_MESSAGE: Record<PlaybookTemplateRunNoIdeasReason, string> = {
  [PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON.allPairsBuilt]:
    'You’ve built all templates possible, you can either build templates from single insights or create new insights.',
  [PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON.allPairsContradict]: 'Cannot build templates',
};

export const getPlaybookTemplateRunNoIdeasMessage = (
  reason?: PlaybookTemplateRunNoIdeasReason | null
): string =>
  reason
    ? PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_MESSAGE[reason]
    : 'This run did not produce any template candidates.';

const INSIGHT_TEMPLATE_STATE_LABEL: Record<InsightTemplateState, string> = {
  [INSIGHT_TEMPLATE_STATE.build]: 'Build template',
  [INSIGHT_TEMPLATE_STATE.building]: 'Building…',
  [INSIGHT_TEMPLATE_STATE.review]: 'Review template',
  [INSIGHT_TEMPLATE_STATE.use]: 'Use template',
};

export const getInsightTemplateStateLabel = (state: InsightTemplateState): string =>
  INSIGHT_TEMPLATE_STATE_LABEL[state];

// The review state is a saved draft. The link offers to create the template until the template is
// published.
const CREATE_TEMPLATE_LINK_LABEL = 'Create template';
const CREATE_TEMPLATE_LINK_TOOLTIP = 'Create a template using this insight';

const INSIGHT_TEMPLATE_LINK_LABEL: Record<InsightTemplateState, string> = {
  [INSIGHT_TEMPLATE_STATE.build]: CREATE_TEMPLATE_LINK_LABEL,
  [INSIGHT_TEMPLATE_STATE.building]: 'Building template…',
  [INSIGHT_TEMPLATE_STATE.review]: CREATE_TEMPLATE_LINK_LABEL,
  [INSIGHT_TEMPLATE_STATE.use]: 'Template',
};

export const getInsightTemplateLinkLabel = (state: InsightTemplateState): string =>
  INSIGHT_TEMPLATE_LINK_LABEL[state];

const INSIGHT_TEMPLATE_LINK_TOOLTIP: Record<InsightTemplateState, string> = {
  [INSIGHT_TEMPLATE_STATE.build]: CREATE_TEMPLATE_LINK_TOOLTIP,
  [INSIGHT_TEMPLATE_STATE.building]: '',
  [INSIGHT_TEMPLATE_STATE.review]: CREATE_TEMPLATE_LINK_TOOLTIP,
  [INSIGHT_TEMPLATE_STATE.use]: 'Template using this insight',
};

export const getInsightTemplateLinkTooltip = (state: InsightTemplateState): string =>
  INSIGHT_TEMPLATE_LINK_TOOLTIP[state];
